import { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuraLayout, Button, Layout, PageTitle } from "lola-framework-ui-test";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import { ErrorAlertIcon as ErrorIcon } from "../../assets/icons";
import { FlowHeroMark } from "../../components/FlowHeroMark";
import { useStore } from "../../store/globalState";
import { useSessionStore } from "../../store/useSessionStore";
import { FUND_REQUEST_ROUTES } from "../../constants";
import { countryDisplayName } from "../../utils/country-display";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";

const PAYER_CODE = "ELEKTRA";

const prettifyPayer = (code?: string): string => {
  if (!code) return "";
  return code
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Gallery-only surface for the fund-request route-unavailable error. */
export const RouteUnavailablePage = memo(() => {
  const { t, i18n } = useTranslation("fund-request");
  const { theme } = useStore();
  const { session } = useSessionStore();
  const navigate = useNavigate();

  const colorConfig = theme.errorView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, iconColors, backgroundIcon } =
    colorConfig;

  const destinationCountry = session.data?.config?.destinationCountry || "MX";
  const destination = useMemo(() => {
    const countryName = countryDisplayName(
      destinationCountry,
      i18n.language || "en"
    );
    const payer = prettifyPayer(PAYER_CODE);
    return payer ? `${payer} (${countryName})` : countryName;
  }, [destinationCountry, i18n.language]);

  const handleBack = () => navigate(FUND_REQUEST_ROUTES.TRANSFER);

  return (
    <AuraLayout colorConfig={colorConfig} className="route-unavailable-page flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark backgroundIcon={backgroundIcon}>
            <ErrorIcon size={60} colors={iconColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("routeUnavailable.errorTitle")}
              highlightColor={title}
              secudnary=""
              secudnaryColor={title}
            />
            <BodyCopy textColor={bodyCopy}>
              {t("routeUnavailable.message", { destination })}
            </BodyCopy>
            <BodyCopy textColor={bodyCopy}>
              {t("routeUnavailable.hint")}
            </BodyCopy>
          </section>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={handleBack}
          >
            {t("routeUnavailable.back")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
